import type { Contributor } from "./contributors";
import { findContributor, getFullName } from "./contributors";
import type { Component } from "./components";
import { components } from "./components";
import type {
  PatternType,
  PatternVariant,
} from "./pattern-types";
import type { Platform, Prototype, SurfaceArea } from "./prototype-types";
import {
  getTemplatePrototypes,
  getTemplatesBySurface,
  prototypes,
} from "./prototypes";

/** Normalized pattern used by /patterns UI and prompt generation. */
export interface PatternViewModel {
  id: string;
  name: string;
  type: PatternType;
  /** Resolved target platform (unset sources normalize to `web`). */
  platform: Platform;
  description: string;
  owner: string;
  path: string;
  thumbnail?: string;
  surfaceArea?: SurfaceArea;
  productionReference?: string;
  figmaLink?: string;
  createdAt?: string;
  lastUpdated?: string;
  variants: PatternVariant[];
  /** Source prototype id when type is page; undefined for components. */
  prototypeId?: string;
}

const DEFAULT_VARIANT_ID = "default";

const DEFAULT_PLATFORM: Platform = "web";

const normalizePlatform = (platform: Platform | undefined): Platform =>
  platform ?? DEFAULT_PLATFORM;

const slugFromPath = (path: string): string =>
  path.replace(/^\//, "").replace(/\//g, "-") || "pattern";

export const buildDefaultVariant = (input: {
  id: string;
  name: string;
  description?: string;
  path: string;
  thumbnail?: string;
  promptInstructions?: string;
}): PatternVariant => ({
  id: DEFAULT_VARIANT_ID,
  name: input.name,
  description: input.description,
  previewPath: input.path,
  thumbnail: input.thumbnail,
  promptInstructions: input.promptInstructions,
});

export const resolvePatternVariants = (
  patternId: string,
  name: string,
  path: string,
  description: string | undefined,
  thumbnail: string | undefined,
  variants: PatternVariant[] | undefined
): PatternVariant[] => {
  if (variants && variants.length > 0) {
    return variants.map((variant) => ({
      ...variant,
      previewPath: variant.previewPath ?? path,
      thumbnail: variant.thumbnail ?? thumbnail,
    }));
  }
  return [
    buildDefaultVariant({
      id: DEFAULT_VARIANT_ID,
      name,
      description,
      path,
      thumbnail,
    }),
  ];
};

export const prototypeToPattern = (prototype: Prototype): PatternViewModel => {
  const type = prototype.patternType ?? "page";
  const variants = resolvePatternVariants(
    prototype.id,
    prototype.name,
    prototype.path,
    prototype.description,
    prototype.thumbnail,
    prototype.variants
  );

  return {
    id: prototype.id,
    name: prototype.name,
    type,
    platform: normalizePlatform(prototype.platform),
    description: prototype.description,
    owner: prototype.owner,
    path: prototype.path,
    thumbnail: prototype.thumbnail,
    surfaceArea: prototype.surfaceArea,
    productionReference: prototype.productionReference,
    figmaLink: prototype.figmaLink,
    createdAt: prototype.createdAt,
    lastUpdated: prototype.lastUpdated,
    variants,
    prototypeId: prototype.id,
  };
};

export const componentToPattern = (component: Component): PatternViewModel => {
  const id = component.id ?? slugFromPath(component.path);
  const variants = resolvePatternVariants(
    id,
    component.name,
    component.path,
    component.description,
    component.thumbnail,
    component.variants
  );

  return {
    id,
    name: component.name,
    type: component.patternType ?? "component",
    platform: normalizePlatform(component.platform),
    description: component.description ?? "",
    owner: component.owner ?? "Component",
    path: component.path,
    thumbnail: component.thumbnail,
    productionReference: component.productionReference,
    variants,
  };
};

export const getPagePatterns = (): PatternViewModel[] =>
  getTemplatePrototypes()
    .filter((prototype) => prototype.surfaceArea !== "Creative")
    .map(prototypeToPattern);

export const getPagePatternsBySurface = (
  surfaceArea: SurfaceArea
): PatternViewModel[] =>
  getTemplatesBySurface(surfaceArea).map(prototypeToPattern);

export const getComponentPatterns = (): PatternViewModel[] =>
  components.map(componentToPattern);

export const getAllPatterns = (): PatternViewModel[] => [
  ...getPagePatterns(),
  ...getComponentPatterns(),
];

export const getPatternById = (patternId: string): PatternViewModel | undefined =>
  getAllPatterns().find((pattern) => pattern.id === patternId);

export const surfaceAreaToPatternCategory = (
  surfaceArea: SurfaceArea
): PatternCategoryId | null => {
  if (
    surfaceArea === "Brand" ||
    surfaceArea === "Retailer" ||
    surfaceArea === "Creative"
  ) {
    return "page";
  }
  return null;
};

export const getPatternCopyPromptPath = (
  category: PatternCategoryId,
  patternId: string
): string => `/patterns/${category}/${patternId}/copy-prompt`;

/** @deprecated Use getPatternCopyPromptPath */
export const getPatternUsePath = getPatternCopyPromptPath;

/** Pattern preview route (component path or template prototype path), not the use-prompt page. */
export const getPatternContextForPreviewPath = (
  pathname: string
): { pattern: PatternViewModel; category: PatternCategoryId } | undefined => {
  if (pathname.startsWith("/patterns")) {
    return undefined;
  }

  const normalizedPath = pathname.split("?")[0] ?? pathname;

  const component = components.find((entry) => entry.path === normalizedPath);
  if (component) {
    return {
      pattern: componentToPattern(component),
      category: "component",
    };
  }

  const template = getTemplatePrototypes().find(
    (prototype) => prototype.path === normalizedPath
  );
  if (!template) {
    return undefined;
  }

  const category = surfaceAreaToPatternCategory(template.surfaceArea);
  if (!category) {
    return undefined;
  }

  return {
    pattern: prototypeToPattern(template),
    category,
  };
};

export const getPatternVariant = (
  pattern: PatternViewModel,
  variantId: string
): PatternVariant | undefined =>
  pattern.variants.find((variant) => variant.id === variantId);

/** Experimental prototypes available when applying a pattern to an existing prototype. */
export const getExperimentalPrototypesForPatternPicker = (): Prototype[] =>
  prototypes.filter(
    (prototype) =>
      prototype.category === "experimental" && !prototype.hidden && !prototype.isExternal
  );

const ownerSlugFromPrototypePath = (path: string): string | undefined => {
  const segment = path.split("/").filter(Boolean)[0];
  return segment ?? undefined;
};

/** Whether a prototype belongs to the given contributor (owner field or URL slug). */
export const isPrototypeOwnedByContributor = (
  prototype: Prototype,
  contributor: Contributor
): boolean => {
  const ownerContributor = findContributor(prototype.owner);
  if (ownerContributor?.slug === contributor.slug) {
    return true;
  }
  return ownerSlugFromPrototypePath(prototype.path) === contributor.slug;
};

/** Experimental prototypes owned by the current contributor only. */
export const getExperimentalPrototypesForCurrentContributor = (
  contributor: Contributor | null | undefined
): Prototype[] => {
  if (!contributor) {
    return [];
  }
  return getExperimentalPrototypesForPatternPicker().filter((prototype) =>
    isPrototypeOwnedByContributor(prototype, contributor)
  );
};

export type PatternCategoryId = "page" | "component";

export type PageSurfaceFilter = "all" | "Brand" | "Retailer";

export const isPatternCategory = (
  value: string | undefined
): value is PatternCategoryId => value === "page" || value === "component";

/** Maps route category param (including legacy slugs) to page | component. */
export const resolvePatternCategory = (
  value: string | undefined
): PatternCategoryId | undefined => {
  if (!value) {
    return undefined;
  }
  if (isPatternCategory(value)) {
    return value;
  }
  if (value === "components") {
    return "component";
  }
  if (value === "brand" || value === "retailer" || value === "creative") {
    return "page";
  }
  return undefined;
};

export const getPatternsForCategory = (
  category: PatternCategoryId
): PatternViewModel[] => {
  if (category === "component") {
    return getComponentPatterns();
  }
  return getPagePatterns();
};

export const patternMatchesSearch = (
  pattern: PatternViewModel,
  query: string
): boolean => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  const creatorName = getFullName(pattern.owner).toLowerCase();
  return (
    pattern.name.toLowerCase().includes(normalized) ||
    pattern.owner.toLowerCase().includes(normalized) ||
    creatorName.includes(normalized)
  );
};

export const patternMatchesPageSurfaceFilter = (
  pattern: PatternViewModel,
  filter: PageSurfaceFilter
): boolean => {
  if (filter === "all") {
    return true;
  }
  return pattern.surfaceArea === filter;
};

export type PlatformFilter = "all" | Platform;

export const patternMatchesPlatformFilter = (
  pattern: PatternViewModel,
  filter: PlatformFilter
): boolean => {
  if (filter === "all") {
    return true;
  }
  return pattern.platform === filter;
};
